import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { useQuery } from 'convex/react'
import { useCallback, useMemo, useState } from 'react'

import type { CalendarTask } from '@/components/inbox-calendar'
import { CATEGORY_COLORS, useCategoryFill } from '@/lib/category-color'

const KEY = (scope: string) => `goaltree.calendarAllGoals.${scope}`

function read(scope: string): boolean {
  try {
    return localStorage.getItem(KEY(scope)) === '1'
  } catch {
    return false
  }
}

// "All goals" calendar mode: remembered per view, feeds tasks/habits from every
// other goal (tinted by category) into the calendar when on.
export function useCalendarAllGoals(
  scope: 'goal' | 'everyday',
  opts: { excludeGoalId: Id<'nodes'>; habits?: boolean },
) {
  const [on, setOn] = useState(() => read(scope))
  const setAllGoals = useCallback(
    (next: boolean) => {
      setOn(next)
      try {
        localStorage.setItem(KEY(scope), next ? '1' : '0')
      } catch {
        // storage unavailable: choice just isn't remembered
      }
    },
    [scope],
  )
  const from = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.getTime() - 60 * 86_400_000
  }, [])
  const rows = useQuery(api.nodes.getCalendarItems, on ? { from } : 'skip')
  const fill = useCategoryFill()
  const items = useMemo(() => {
    if (!on || !rows) return []
    return rows
      .filter(
        (r) =>
          r.goalId !== opts.excludeGoalId &&
          (opts.habits === undefined || r.isHabit === opts.habits),
      )
      .map(
        (r): CalendarTask => ({
          _id: r._id,
          title: r.title,
          description: r.description ?? undefined,
          completedAt: r.completedAt,
          effort: r.effort,
          recurs: r.recurs,
          recursPeriod: r.recursPeriod,
          recursDays: r.recursDays,
          recurrenceDay: r.recurrenceDay,
          recurrenceTime: r.recurrenceTime,
          dueDate: r.dueDate,
          durationMinutes: r.durationMinutes,
          allDay: r.allDay,
          status: r.status,
          parentId: r.goalId,
          goalId: r.goalId,
          goalTitle: r.goalTitle,
          goalCategory: r.category,
          isHabit: r.isHabit,
          isInbox: r.isInbox,
          goalColor: fill(CATEGORY_COLORS[r.category] ?? '#8b8b95'),
        }),
      )
  }, [on, rows, opts.excludeGoalId, opts.habits, fill])
  return { allGoals: on, setAllGoals, items }
}
